import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { ASSETS, COMPANY } from "../data/content";

const stats = [
  { value: "109+", label: "Countries Served" },
  { value: "7,00,000+", label: "Inspection Calls" },
  { value: "500+", label: "Projects" },
  { value: "9+", label: "Years Expertise" },
];

const images = [ASSETS.hero, ASSETS.welding, ASSETS.pipeline];

export const Hero = () => {
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0A192F]"
    >
      {images.map((src, index) => (
        <motion.img
          key={src}
          src={src}
          alt={`Industrial engineering workshop ${index + 1}`}
          initial={{ opacity: index === 0 ? 1 : 0 }}
          animate={{ opacity: index === currentImage ? 1 : 0 }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/88 to-[#0A192F]/45" />
    <div
      className="absolute inset-0 opacity-[0.18]"
      style={{
        backgroundImage:
          "repeating-linear-gradient(135deg, rgba(255,255,255,.07) 0 1px, transparent 1px 14px)",
      }}
    />

    <div className="relative mx-auto w-full max-w-[1400px] px-5 lg:px-10 pt-32 pb-20">
      <div className="max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5"
        >
          <ShieldCheck size={14} className="text-[#D4AF37]" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#E8C860]">
            {COMPANY.certification}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white"
        >
          Global Inspection &<br />
          <span className="text-[#D4AF37]">Engineering Excellence</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-slate-300"
        >
          A worldwide network of engineering expert executives delivering
          second- and third-party inspection, expediting, welding &amp; NDT
          consultancy and manpower solutions — with assured quality at every
          step.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-4 font-display text-sm font-semibold uppercase tracking-[0.24em] text-[#22D3EE]"
        >
          {COMPANY.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.38 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#services"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#E8C860] transition-colors duration-300"
          >
            Explore Services
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform duration-300"
            />
          </a>
          <a
            href="#contact"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center rounded-full border border-white/30 px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
          >
            Contact Us
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-16 flex flex-wrap gap-x-14 gap-y-8 border-t border-white/10 pt-8"
          data-testid="hero-stats"
        >
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-display text-3xl font-extrabold text-white">
                {s.value}
              </div>
              <div className="mt-1 text-[11px] font-medium uppercase tracking-[0.2em] text-slate-400">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
  );
};
